/**
 * A hook to access category data.
 *
 * - categoryId
 *   - If no `categoryId` is provided, it will try to find the currently selected category and return it.
 *   - If no `categoryId` is provided and no category is found, it will try to find the currently selected article and return article.category.
 *   - Where `categoryId` is provided and if home is defined (see [[useHome]]), it will try match `categoryId` to one of home.categories and try to return the find result.
 *   - If none of the above are found, returns `undefined`.
 *
 * - usePageCategory
 *   - If true, hook will look up the category of the page currently being viewed.
 *
 */
import { useContext } from 'react';
import { useArticleFragment } from './useArticleFragment';
import { CategoryContext } from '../contexts';
import { useSelector } from '../actions/types';
import { usePageInfo } from '../hooks/usePageInfo';
import { categoryTree } from '../selectors';
import { throwIfDevelop } from '../utils';
/**
 *
 * # useCategory
 *
 * Usage example:
 * ```javascript
 * import { useCategory } from "@elevio/kb-kit/lib/hooks";
 *
 * function MyComponent({ categoryId, usePageCategory }) {
 *   const {
 *     path,
 *     parentCategory,
 *     subCategories,
 *     articles,
 *     id,
 *     title,
 *     slug,
 *     featuredImageUrl,
 *   } = useCategory({ categoryId, usePageCategory });
 *
 *   console.log(path); // string
 *   console.log(parentCategory); // string | null
 *   console.log(subCategories); // Array<string>
 *   console.log(articles); // Array<string>
 *   console.log(id); // string
 *   console.log(title); // string
 *   console.log(slug); // string
 *   console.log(featuredImageUrl); // string | undefined
 *
 *   return (
 *     ...
 *   )
 * }
 * ```
 */
export function useCategory({ categoryId, usePageCategory, } = {}) {
    var _a, _b;
    const categoryContextId = useContext(CategoryContext);
    const article = useArticleFragment();
    const pageInfo = usePageInfo();
    const categoryPageId = (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.pageType) === 'category' ? pageInfo.categoryId : undefined;
    let categoryToLookup;
    // If we are looking up the page category...
    if (usePageCategory) {
        if (!categoryPageId) {
            throwIfDevelop(`trying to use category from page but not on a category page: ${JSON.stringify(pageInfo)}`);
        }
        categoryToLookup = categoryPageId;
    }
    else {
        categoryToLookup =
            (_b = (_a = categoryId !== null && categoryId !== void 0 ? categoryId : categoryContextId) !== null && _a !== void 0 ? _a : article === null || article === void 0 ? void 0 : article.categoryId) !== null && _b !== void 0 ? _b : categoryPageId;
    }
    const category = useSelector((state) => {
        if (!categoryToLookup || categoryId === 'root')
            return null;
        return categoryTree.category(state, categoryToLookup);
    });
    return category;
}
//# sourceMappingURL=useCategory.js.map